import { isRequestError } from 'utils/requestError';
import './TransferBIDVInternal.scss';
import { InputText, InputNumber, Loading, ModalPinCode } from 'components';
import { Button, InputWraper, useForm } from 'modules';
import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import {
  useBidvSubmitTransferMutation,
  useBidvLookupAccountQuery,
  useCheckPinCodeMutation,
} from 'services';
import { SubmitTransferData } from 'services/openapi';
import BidvTransferResultModal from '../components/BidvTransferResultModal';

const TransferBIDVInternal = () => {
  const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
  const [transferResult, setTransferResult] =
    useState<SubmitTransferData | null>(null);
  const pinCodeRef = useRef('');

  const { mutateAsync: mutateAsyncSubmit, isLoading: isLoadingSubmit } =
    useBidvSubmitTransferMutation();
  const {
    mutateAsync: mutateAsyncCheckPinCode,
    isLoading: isLoadingCheckPinCode,
  } = useCheckPinCodeMutation();

  const {
    handleSubmit,
    getInputProps,
    resetForm,
    isAllFilled,
    setValues,
    getValue: getFormValue,
  } = useForm({
    structure: [
      {
        name: 'amount',
        label: 'Số tiền',
        validate: Yup.number()
          .typeError('Vui lòng nhập số tiền hợp lệ')
          .min(2000, 'Số tiền phải lớn hơn 2000')
          .required('Vui lòng nhập số tiền'),
      },
      {
        name: 'beneficiaryAccount',
        label: 'Số tài khoản',
        validate: Yup.string().required('Vui lòng nhập số tài khoản'),
      },
      {
        name: 'beneficiaryName',
        label: 'Tên tài khoản',
        validate: Yup.string().required('Vui lòng nhập tên tài khoản'),
        isDisabled: true,
      },
      {
        name: 'remark',
        label: 'Nội dung',
        validate: Yup.string().required('Vui lòng nhập nội dung'),
      },
    ],
    onSubmit: async (values) => {
      try {
        const res = await mutateAsyncSubmit({
          transferMethod: 'I',
          beneficiaryAccount: values.beneficiaryAccount?.trim(),
          beneficiaryName: values.beneficiaryName?.trim(),
          bankCode: '01202001',
          amount: Number(values.amount),
          remark: values.remark?.trim(),
          pinCode: pinCodeRef.current,
        });
        setTransferResult(res?.data ?? null);
        setValues({
          amount: 0,
          beneficiaryAccount: '',
          beneficiaryName: '',
          remark: '',
        });
        resetForm();
      } catch (error) {
        console.error('Submit error:', error);
      }
    },
  });

  const { isFetching: isFetchingLookupAccount, refetch: lookupAccount } =
    useBidvLookupAccountQuery(
      {
        transferMethod: 'I',
        accountNo: getFormValue('beneficiaryAccount')?.trim() || '',
      },
      false,
    );

  const handleLookupAccount = async () => {
    const account = getFormValue('beneficiaryAccount');
    if (!account?.trim()) {
      toast.error('Vui lòng nhập số tài khoản');
      return;
    }
    try {
      setValues({ beneficiaryName: '' });
      const result = await lookupAccount();
      if (result.isError) return;
      if (result.data?.data?.accountName) {
        setValues({ beneficiaryName: result.data.data.accountName });
        toast.success('Đã tìm thấy thông tin tài khoản BIDV', {
          duration: 1500,
        });
      } else {
        toast.error('Không tìm thấy thông tin tài khoản BIDV');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      if (!isRequestError(error)) {
        toast.error('Không tìm thấy thông tin tài khoản BIDV');
      }
    }
  };

  async function handleCheckPinCode(pinCode: string) {
    try {
      const result = await mutateAsyncCheckPinCode({ pinCode });
      if (!result?.isValid) {
        result?.message?.forEach((msg: string) => toast.error(msg));
      }
      setIsShowPopup(false);
      if (result?.isValid) {
        pinCodeRef.current = pinCode;
        await handleSubmit();
      }
    } catch (error) {
      setIsShowPopup(false);
      console.error(error);
    }
  }

  return (
    <div className='bidv-transfer'>
      <Loading isLoading={isLoadingSubmit}>
        <div className='row container-fluid-bidv-transfer'>
          <h1>Chuyển khoản nội bộ BIDV</h1>
          <InputWraper
            isRequired
            className='col-lg-12'
            inputProps={getInputProps('amount')}
            component={InputNumber}
          />
          <InputWraper
            isRequired
            className='col-lg-12 bidv-transfer__account-lookup'
            inputProps={{
              ...getInputProps('beneficiaryAccount'),
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLookupAccount();
                }
              },
            }}
            renderInput={(props) => (
              <>
                <InputText {...props} />
                <Button
                  isVisible
                  label='Kiểm tra'
                  type='button'
                  buttonType='primary'
                  isLoading={isFetchingLookupAccount}
                  disabled={isFetchingLookupAccount}
                  onClick={handleLookupAccount}
                />
              </>
            )}
          />
          <InputWraper
            isRequired
            className='col-lg-12'
            inputProps={getInputProps('beneficiaryName')}
            component={InputText}
          />
          <InputWraper
            isRequired
            className='col-lg-12'
            inputProps={getInputProps('remark')}
            component={InputText}
          />
        </div>

        <p className='text-danger'>
          * Chúng tôi không chịu trách nhiệm cho việc điền sai thông tin nhận
          tiền hoặc bị hack tài khoản
        </p>
        <div className='wrap-button-create-user'>
          <Button
            label='Chuyển tiền'
            type='submit'
            onClick={() => setIsShowPopup(true)}
            buttonType='success'
            disabled={isLoadingSubmit || !isAllFilled()}
          />
        </div>
      </Loading>

      {transferResult && (
        <BidvTransferResultModal
          submitData={transferResult}
          onClose={() => setTransferResult(null)}
        />
      )}

      <ModalPinCode
        isOpen={isShowPopup}
        isLoading={isLoadingCheckPinCode}
        onClose={() => setIsShowPopup(false)}
        onConfirm={handleCheckPinCode}
      />
    </div>
  );
};

export default TransferBIDVInternal;
